/**
 * PMS Auth Module
 *
 * Wraps all Supabase Auth operations so the raw client
 * is never imported outside `src/integrations/pms/`.
 */

import { supabase } from '@/integrations/supabase/client';
import type { User, Session, AuthChangeEvent } from '@supabase/supabase-js';

// ── Types re-exported for consumers ─────────────────────────────────

export type { User, Session, AuthChangeEvent };

export interface AuthCredentials {
  email: string;
  password: string;
}

export interface SignUpCredentials extends AuthCredentials {
  fullName?: string;
  metadata?: Record<string, unknown>;
}

// ── Auth facade ─────────────────────────────────────────────────────

export const pmsAuth = {
  /** Get the current user (cached session check). */
  getUser: async () => {
    const { data, error } = await supabase.auth.getUser();
    if (error) throw error;
    return data.user;
  },

  /** Get the current user without throwing (returns null). */
  getUserSafe: async (): Promise<User | null> => {
    const { data } = await supabase.auth.getUser();
    return data.user ?? null;
  },

  /** Get current session. */
  getSession: async () => {
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    return data.session;
  },

  /** Sign in with email + password. */
  signInWithPassword: async (credentials: AuthCredentials) => {
    const { data, error } = await supabase.auth.signInWithPassword(credentials);
    if (error) throw error;
    return data;
  },

  /** Sign up with email + password. */
  signUp: async ({ email, password, fullName, metadata }: SignUpCredentials, options?: { emailRedirectTo?: string }) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName, ...metadata },
        emailRedirectTo: options?.emailRedirectTo,
      },
    });
    if (error) throw error;
    return data;
  },

  /** Sign out. */
  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  /** Set session (used by Lovable OAuth flow). */
  setSession: async (tokens: { access_token: string; refresh_token: string }) => {
    const { data, error } = await supabase.auth.setSession(tokens);
    if (error) throw error;
    return data;
  },

  /** Subscribe to auth state changes. Returns an unsubscribe function. */
  onAuthStateChange: (callback: (event: AuthChangeEvent, session: Session | null) => void) => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(callback);
    return () => subscription.unsubscribe();
  },

  /** Reset password. */
  resetPasswordForEmail: async (email: string, redirectTo?: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    });
    if (error) throw error;
  },

  /** Update user (password change, metadata, etc.). */
  updateUser: async (attributes: { password?: string; data?: Record<string, unknown> }) => {
    const { data, error } = await supabase.auth.updateUser(attributes);
    if (error) throw error;
    return data;
  },

  /** Refresh session from a refresh token. */
  refreshSession: async (params: { refresh_token: string }) => {
    const { data, error } = await supabase.auth.refreshSession(params);
    if (error) throw error;
    return data;
  },
};
